import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { setOverlay } from '../../../redux/slices/appStateSlice';
import { useInternalNavigate } from '../../../utils/navigate/useNavigate';
import { setApplicationSubmissionResponse } from '../../../redux/slices/applicationSubmissionSlice';
import GetQAApplyRequestBody from '../../../utils/prospectData/getQAApplyRequestBody';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-react';
import config from '../../../temp/config';
import { pushServiceDownError } from '../../../utils/analytics/datalayer';

const QuoteApplyLogic = ({ fields, applyEndpoint, agentDetails, minDisplayTime }) => {
  const [hasComplete, setHasComplete] = useState(false);
  const [canClose, setCanClose] = useState(!minDisplayTime);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const dispatch = useDispatch();
  const navigate = useInternalNavigate();

  if (isEditorActive()) {
    return null;
  }

  const requestBody = useSelector(
    state => state.applicationSubmission.applicationSubmissionRequestBody
  );

  useEffect(() => {
    if (minDisplayTime) {
      const timeout = setTimeout(() => setCanClose(true), minDisplayTime * 1000);
      return () => clearTimeout(timeout);
    }
  }, []);

  useEffect(() => {
    if (!requestBody || !applyEndpoint || hasSubmitted) {
      return;
    }

    const submitApplication = (recaptchaToken = '') => {
      const fullRequestBody = {
        ...agentDetails,
        ...requestBody,
        recaptchaToken,
      };

      setHasSubmitted(true);

      fetch(applyEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fullRequestBody),
      })
        .then(async res => {
          const contentType = res.headers.get('Content-Type') || '';

          if (!res.ok) {
            await pushServiceDownError(applyEndpoint, res);
            throw new Error('Failed to submit application');
          }

          let data;
          if (contentType.includes('application/json')) {
            try {
              data = await res.json();

              if (data.shouldPreventSale) {
                navigate(fields.existingPlanPage.value.href);
              }
            } catch (err) {
              data = { message: await res.text() };
            }
          } else {
            data = { message: await res.text() };
          }

          dispatch(setApplicationSubmissionResponse(data));
          setHasComplete(true);
        })
        .catch(err => {
          if (err?.message !== 'Failed to submit application') {
            pushServiceDownError(applyEndpoint, null, err);
          }
          dispatch(setOverlay(null));
          navigate(fields.servicedownPage?.value?.href);
        });
    };

    if (typeof window !== 'undefined' && config.recaptcha.toggle && window.grecaptcha) {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(config.recaptcha.sitekey, { action: 'submit' }).then(token => {
          submitApplication(token);
        });
      });
    } else {
      submitApplication();
    }
  }, [requestBody]);

  useEffect(() => {
    if (hasComplete && canClose) {
      dispatch(setOverlay(null));
      navigate(fields.successPage?.value?.href);
    }
  }, [hasComplete, canClose]);

  return <GetQAApplyRequestBody />;
};

const mapStateToProps = (state, props) => ({
  ...props,
  applyEndpoint: state.appState?.config?.endpoints?.apply,
  agentDetails: state.agentDetails,
});

export default connect(mapStateToProps)(QuoteApplyLogic);
